import {IWinRatioConfig, WinRatioType} from "@nolimitcity/slot-game/bin/core/winpresentation/WinRatio";
import {GGSoundConfig} from "../GGSoundConfig";
import {SlotGame} from "@nolimitcity/slot-game/bin/core/SlotGame";
import {INolimitAudioPlayer} from "@nolimitcity/slot-audio/bin/INolimitAudioPlayer";
import {Logger} from "@nolimitcity/slot-launcher/bin/utils/Logger";
import {ScreenEvent} from "@nolimitcity/slot-game/bin/core/screen/event/ScreenEvent";
import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {TimelineLite, TweenLite} from "gsap";

export class GGSoundPlayer {
    private static isNewRound:boolean = true;
    private static playedOnce:boolean = false;
    private static currentLoop:string = "";
    private static landingSounds:string[] = [];
    private static winSounds:string[] = [];
    private static abortOnWinExplosion:boolean = false;

    private static get soundPlayer():INolimitAudioPlayer {
        return <INolimitAudioPlayer>SlotGame.sound;
    }

    constructor() {
        EventHandler.addEventListener(this, ScreenEvent.GAME_READY, () => this.onReady());
    }

    private onReady() {
        GGSoundPlayer.playAmbientLoop(GGSoundConfig.instance.MAIN_GAME_AMBIANCE);
    }

    public static playAmbientLoop(name:string, volume?:number):void {
        this.currentLoop = name;
        Logger.logDev("playAmbientLoop ", GGSoundPlayer.currentLoop);
        this.soundPlayer.playTrack({name : this.currentLoop, volume : 0});
        this.soundPlayer.fadeTrack({
            name : this.currentLoop,
            from : 0,
            to : 1,
            duration : 1000
        });
    }

    public playSoundEffect(name:string, repeat?:number, volume?:number):void {
        Logger.logDev("starting sound ", name);

        GGSoundPlayer.soundPlayer.playTrack({
            name : name,
            repeat : repeat,
            volume : volume
        });
    }

    public stopSoundEffect(name:string):void {
        Logger.logDev("stopping sound ", name);
        GGSoundPlayer.soundPlayer.stopTrack(name);
    }

    public playFSAmbianceSound(roundType:number):void {
        const name:string = roundType === 1 ? GGSoundConfig.instance.FREE_SPIN_AMBIANCE : GGSoundConfig.instance.SUPER_FREE_SPIN_AMBIANCE;
        GGSoundPlayer.switchLoopSound(name, 0,0);
    }

    public playAnticipationSound() {
        GGSoundPlayer.fadeOutCurrentLoop();
        this.playSoundEffect(GGSoundConfig.ANTICIPATION, 10, 1);
    }

    public stopAnticipationSound():void {
        GGSoundPlayer.fadeInCurrentLoop();
        this.stopSoundEffect(GGSoundConfig.ANTICIPATION);
    }

    public playBWAmbianceSound():void {
        GGSoundPlayer.fadeOutCurrentLoop(500);
        //this.playSoundEffect(GGSoundConfig.BIG_WIN_LOOP, 3, 1);

    }

    public stopBWAmbianceSound():void {
        GGSoundPlayer.fadeInCurrentLoop(1500);
        //this.stopSoundEffect(GGSoundConfig.BIG_WIN_LOOP);
    }

    public playCapWinStartSound():void {
        let soundName:string = GGSoundConfig.CAP_WIN_START;
        GGSoundPlayer.fadeOutCurrentLoop(1000);
        this.playSoundEffect(soundName);
    }

    public playCapWinLoopSound():void {
        this.playSoundEffect(GGSoundConfig.CAP_WIN_LOOP, 1000, 1);
    }

    public playCapWinContinueSound():void {
        GGSoundPlayer.currentLoop = GGSoundConfig.instance.MAIN_GAME_AMBIANCE;
        GGSoundPlayer.fadeInCurrentLoop(1000);
        this.playSoundEffect(GGSoundConfig.CAP_WIN_CONTINUE);
        this.stopSoundEffect(GGSoundConfig.CAP_WIN_LOOP);
    }

    public static fadeOutCurrentLoop(fadeDuration:number = 300):void {
        this.fadeOutMusic(this.currentLoop, fadeDuration);
    }

    public static fadeInCurrentLoop(fadeDuration:number = 300):void {
        Logger.logDev("fadeInCurrentLoop ", this.currentLoop + ", duration: " + fadeDuration);
        this.fadeInMusic(this.currentLoop, fadeDuration);
    }

    public playUpgradeFSAmbianceSound():void {
        Logger.logDev("playUpgradeFSAmbianceSound ", GGSoundPlayer.currentLoop);
        const musicBefore:string = GGSoundPlayer.currentLoop;
        GGSoundPlayer.fadeOutCurrentLoop(1);
        const tl:TimelineLite = new TimelineLite({
            onComplete:()=>{this.stopSoundEffect(musicBefore)}
        });
        tl.add(TweenLite.to(this, 0.1,{}));
        GGSoundPlayer.currentLoop = GGSoundConfig.instance.SUPER_FREE_SPIN_AMBIANCE;
        GGSoundPlayer.fadeInCurrentLoop(1);
    }

    public stopFSMusic():void {
        Logger.logDev("stopFSMusic ", GGSoundPlayer.currentLoop);
        if(GGSoundPlayer.currentLoop === GGSoundConfig.instance.MAIN_GAME_AMBIANCE){
            return;
        }
        const musicBefore:string = GGSoundPlayer.currentLoop;
        GGSoundPlayer.fadeOutCurrentLoop(20);
        const tl:TimelineLite = new TimelineLite({
            onComplete:()=>{this.stopSoundEffect(musicBefore)}
        });
        tl.add(TweenLite.to(this, 0.1,{}));
        GGSoundPlayer.currentLoop = GGSoundConfig.instance.MAIN_GAME_AMBIANCE;
        GGSoundPlayer.fadeInCurrentLoop(1500);
    }

    public prepareLandingSounds() {
        GGSoundPlayer.winSounds = [];
        const sounds:string[] = GGSoundConfig.REELS_DEFAULT_BOUNCE;
        GGSoundPlayer.landingSounds = sounds.sort(() => 0.5 - Math.random());
    }

    public playLandingSound(reelId:number) {
        this.playSoundEffect(GGSoundPlayer.landingSounds[reelId]);
    }

    public playNearWinLandingSound(isSingle:boolean) {
        const soundName:string = isSingle ? GGSoundConfig.ANTICIPATION_SYMBOL_LANDING : GGSoundConfig.ANTICIPATION_SYMBOL_LANDING_MULTI;
        this.playSoundEffect(soundName);
    }

    public static fadeInMusic(soundName:string, duration:number) {
        this.soundPlayer.playTrack({name : soundName, volume : 0});
        this.soundPlayer.fadeTrack({
            name : soundName,
            duration : duration,
            to : 1
        });
    }

    public static fadeOutMusic(soundName:string, duration:number) {
        this.soundPlayer.fadeTrack({
            name : soundName,
            duration : duration,
            to : 0
        });
    }

    public static switchLoopSound(soundName:string, fadeInDuration:number, fadeOutDuration:number) {
        Logger.logDev("switching to ", soundName);
        Logger.logDev("currentLoop is ", this.currentLoop);
        GGSoundPlayer.fadeOutCurrentLoop(   1);
        GGSoundPlayer.currentLoop = soundName;
        GGSoundPlayer.fadeInCurrentLoop(1);
    }

    public playNormalWinSound(winRatioConfig:IWinRatioConfig) {
        let soundName:string = "";
        switch(winRatioConfig.type) {
            case WinRatioType.TINY:
                soundName = GGSoundConfig.TINY_WIN;
                break;
            case WinRatioType.SMALL:
                soundName = GGSoundConfig.SMALL_WIN;
                break;
            case WinRatioType.MEDIUM:
                soundName = GGSoundConfig.MEDIUM_WIN;
                break;
            case WinRatioType.LARGE:
                soundName = GGSoundConfig.LARGE_WIN;
                break;
            default:
                soundName = "";
                break;
        }

        this.playSoundEffect(soundName);
    }

    public startNewRound() {
        GGSoundPlayer.isNewRound = true;
        GGSoundPlayer.playedOnce = false;
        GGSoundPlayer.abortOnWinExplosion =false;
    }

    public playLargerWinExplosionSound(randomStart:number, reelId:number):void {
        if(GGSoundPlayer.abortOnWinExplosion){
            return;
        }
        const soundId:number = (randomStart + reelId) % 6;
        const soundName:string = GGSoundConfig.SYMBOL_WIN_EXPLOSIONS[soundId];
        this.playSoundEffect(soundName);
        GGSoundPlayer.winSounds.push(soundName);
    }

    public playSmallWinExplosionSound():void {
        const soundName:string = `winExplosionMany${Math.floor(Math.random() * 2) + 1}`;
        this.playSoundEffect(soundName);
        GGSoundPlayer.winSounds.push(soundName);
    }

    public playBombThrowSound(transformNum:number):void {
        if(!GGSoundPlayer.isNewRound) {
            return;
        }
        GGSoundPlayer.isNewRound = false;
        const soundName:string = transformNum > 1 ? GGSoundConfig.BOMB_THROW_MANY : GGSoundConfig.BOMB_THROW_ONE;
        this.playSoundEffect(soundName);
    }

    public playBombThrowHighLightSound():void {
        const soundName:string = GGSoundConfig.BOMB_THROW_HIGHLIGHT;
        this.playSoundEffect(soundName);
    }

    public playBombLandingSound():void {
        const soundName:string = GGSoundConfig.BOMB_LANDING;
        this.playSoundEffect(soundName);
    }

    public playWildBombHit():void {
        const soundName:string = GGSoundConfig.MToWild;
        this.playSoundEffect(soundName);
    }

    public playWinRowMidSym():void {
        const soundName:string = GGSoundConfig.WIN_ROW_M;
        this.playSoundEffect(soundName);
        GGSoundPlayer.winSounds.push(soundName);
    }

    public playBetlineWin():void {
        const soundName:string = "winRow1";
        this.playSoundEffect(soundName);
        GGSoundPlayer.winSounds.push(soundName);
    }

    public playFSIntroSound(mode:string, withBom:boolean=false):void {
        let soundName:string = `fsIntro${withBom?"E":""}${mode[0]==="s"?"GFS":"FMS"}`;
        this.playSoundEffect(soundName);
    }

    public playEvil4PopUpSound():void {
        let soundName:string = GGSoundConfig.EVIL4_POPUP;
        this.playSoundEffect(soundName);
    }

    public playEvil4BlastSound():void {
        let soundName:string = GGSoundConfig.EVIL4_BLAST;
        this.playSoundEffect(soundName);
    }

    public playFsOutroSound(hasWin:boolean):void {
        let soundName:string = hasWin?GGSoundConfig.FS_OUTRO_WIN:GGSoundConfig.FS_OUTRO_NO_WIN;
        this.playSoundEffect(soundName);
    }

    public playFsOutroCoinSound():void {
        let soundName:string = GGSoundConfig.COUNTUP;
        this.playSoundEffect(soundName);
    }

    public playFsOutroCoinEndSound():void {
        let soundName:string = GGSoundConfig.MEDIUM_WIN;
        this.stopSoundEffect(GGSoundConfig.COUNTUP);
        this.playSoundEffect(soundName);
    }

    public playFsButtonClickSound():void {
        let soundName:string = GGSoundConfig.FS_BUTTON_CLICK;
        this.playSoundEffect(soundName);
    }

    public playCameraPanningSound(isRestore:boolean = false):void {
        if(isRestore){
            return;
        }
        let soundName:string = GGSoundConfig.FS_Camera_Panning;
        this.playSoundEffect(soundName);
    }

    public playDeadWildLandingSound(counter:number):void {
        let soundName:string = counter>2?GGSoundConfig.DEADWILD_LANDING_WIN:GGSoundConfig.DEADWILD_LANDING;
        if(counter === 2){
            soundName = GGSoundConfig.DEADWILD_LANDING_2
        }
        this.playSoundEffect(soundName);
    }

    public playGoblinPopUpSound():void {
        let soundName:string = GGSoundConfig.GOBLIN_POPUP;
        this.playSoundEffect(soundName);
    }

    public playGoblinAnticipationSound(hasWon:boolean):void {
        let soundName:string = hasWon?GGSoundConfig.GOBLIN_ANTICIPATION_LONG:GGSoundConfig.GOBLIN_ANTICIPATION_SHORT;
        this.playSoundEffect(soundName);
    }

    public playGoblinLatPopUpSound(hasWon:boolean, anticipationId:number, symName:string):void {
        if(anticipationId === 0){
            return;
        }

        if(hasWon) {
            const sounds:string[] = anticipationId > 1 ? GGSoundConfig.LAST_GOBLIN_START_EVIL4 : GGSoundConfig.LAST_TWO_GOBLIN_START_EVIL4;
            let soundName:string = sounds[parseInt(symName[1]) - 1];
            this.playSoundEffect(soundName);
        }

        this.playSoundEffect(GGSoundConfig.GOBLIN_POPUP);
    }

    public playGoblinThrowBombSound():void {
        let soundName:string = GGSoundConfig.GOBLIN_THROW;
        this.playSoundEffect(soundName);
    }

    public playGoblinToSkeletonSound():void {
        let soundName:string = GGSoundConfig.GOBLIN_TO_SKELETON;
        this.playSoundEffect(soundName);
    }

    public playEvil4GoblinLandsOnReel1Sound():void {
        let soundName:string = GGSoundConfig.EVEL4_LAND_ON_REEL1;
        this.playSoundEffect(soundName);
    }

    public playFSCounterUpdateSound(increase:boolean):void {
        let soundName:string = GGSoundConfig.FS_COUNTER_UPDATE;
        this.playSoundEffect(soundName);
    }

    public playEvil4MultiplierUpdateSound():void {
        let soundName:string = GGSoundConfig.EVEL4_MULTIPLIER_UPDATE;
        this.playSoundEffect(soundName);
    }

    public playRetriggerSound():void {
        let soundName:string = GGSoundConfig.GFS_RETRIGGER;
        this.playSoundEffect(soundName);
    }

    public stopWinSoundsOnAbort():void {
        GGSoundPlayer.abortOnWinExplosion = true;
        GGSoundConfig.SYMBOL_WIN_EXPLOSIONS.forEach((name:string)=>{
            this.stopSoundEffect(name);
        });
        this.playSmallWinExplosionSound();
    }
}